<template>
    <loading v-if="loading"></loading>
    <div v-else>
        <p class="mb-4">{{ frameworkDescription }} <a href="/admin/prioritize" class="text-muted">Legacy prioritization function.</a></p>

        <div :class="{ 'fullscreen-component': isFullScreen }">
        <add-post-modal
            ref="addPostModal"
            :status="newStatusId"
            :statuses="statuses"
            :categories="categories"
            :is-admin="true"
            :is-admin-form="true"
            :modal-title="'Add New Post'"
            @post-added="onPostAdded">
        </add-post-modal>

        <div class="d-flex justify-content-between mb-3">
            <div class="d-flex">
                <div>
                    <b-form-select v-model="prioritizationFramework" :options="frameworkOptions" @change="setFramework"></b-form-select>
                </div>
                <div class="ml-2">
                    <b-button variant="outline" v-b-modal.edit-statuses-modal>Select Statuses</b-button>

                    <b-button @click="toggleFullScreen" variant="outline" class="mr-2">
                        <b-icon :icon="isFullScreen ? 'fullscreen-exit' : 'fullscreen'"></b-icon>
                        {{ isFullScreen ? 'Exit Full Screen' : 'Full Screen' }}
                    </b-button>
                    <b-button @click="exportCurrentStatuses" variant="outline">
                        <b-icon icon="download"></b-icon>
                        Export
                    </b-button>
                    <b-button variant="outline" @click="openAddPostModal">Add Post</b-button>
                </div>
            </div>
            <div class="d-flex">
                <b-button-toolbar>
                    <b-input-group class="mr-2">
                        <b-form-input v-model="searchQuery" placeholder="Search..." style="padding: 0rem 1rem;"></b-form-input>
                    </b-input-group>

                    <b-button class="mr-2" variant="outline" @click="reorderByScore">Reorder by Score</b-button>
                    <b-button variant="outline" @click="showUnprioritized = !showUnprioritized">
                        {{ showUnprioritized ? 'Show All' : 'Show Unscored Only' }}
                    </b-button>
                </b-button-toolbar>
            </div>
        </div>

        <div class="mb-3 small text-muted">
            <span class="mr-3">Total: {{ prioritizedCount }}/{{ totalCount }}</span>
            <span v-if="prioritizationFramework === 'MoSCoW'">
                MoSCoW Distribution:
                M: {{ getMoscowPercentage('M') }}%,
                S: {{ getMoscowPercentage('S') }}%,
                C: {{ getMoscowPercentage('C') }}%,
                W: {{ getMoscowPercentage('W') }}%
            </span>
        </div>

        <b-table :items="filteredPosts" :fields="fields" striped hover sticky-header="500px" @head-clicked="sortByField">
            <template #head()="data">
                <span class="d-flex align-items-center">
                    {{ data.label }}
                    <b-icon icon="sort-down" class="ml-2 sort-icon"></b-icon>
                </span>
            </template>

            <template #cell(title)="data">
                <a :href="data.item.url">{{ data.item.title }}</a>
            </template>

            <template #cell(votes_and_conversion)="data">
                <div>Votes: {{ data.item.votes_count }}</div>
                <div>
                    Conversion: {{ data.item.conversion }}%
                    <span v-b-tooltip.hover title="Measures popularity of feature ideas. Higher rates indicate stronger user demand. Calculated as: (Upvotes / Views) * 100% "><b-icon icon="question-circle"></b-icon></span>
                </div>
            </template>

            <template #cell(prioritization_reach)="data" v-if="prioritizationFramework === 'RICE'">
                <template v-if="!data.item.editing || data.item.editingField !== 'prioritization_reach'">
                    <div @click="startEditing(data.item, 'prioritization_reach')" style="cursor: pointer">
                        <i v-for="i in 5" :key="i" class="bi bi-circle-fill mr-1"
                           :class="i <= Math.round((data.item.prioritization_reach || 0) / 2) ? 'text-success' : 'text-muted'"></i>
                    </div>
                </template>
                <div v-else>
                    <b-form-input type="number" v-model.number="data.item.prioritization_reach"
                                  min="1" max="10" step="1"
                                  @blur="validateAndUpdate($event, data.item, 'prioritization_reach')"
                                  @keyup.enter="validateAndUpdate($event, data.item, 'prioritization_reach')"></b-form-input>
                    <small class="text-danger">{{ getErrorMessage(data.item, 'prioritization_reach') }}</small>
                </div>
            </template>

            <template #cell(prioritization_impact)="data">
                <template v-if="!data.item.editing || data.item.editingField !== 'prioritization_impact'">
                    <div @click="startEditing(data.item, 'prioritization_impact')" style="cursor: pointer">
                        <i v-for="i in 5" :key="i" class="bi bi-circle-fill mr-1"
                           :class="i <= Math.round((data.item.prioritization_impact || 0) / 2) ? 'text-primary' : 'text-muted'"></i>
                    </div>
                </template>
                <div v-else>
                    <b-form-input type="number" v-model.number="data.item.prioritization_impact"
                                  min="1" max="10" step="1"
                                  @blur="validateAndUpdate($event, data.item, 'prioritization_impact')"
                                  @keyup.enter="validateAndUpdate($event, data.item, 'prioritization_impact')"></b-form-input>
                    <small class="text-danger">{{ getErrorMessage(data.item, 'prioritization_impact') }}</small>
                </div>
            </template>

            <template #cell(prioritization_confidence)="data" v-if="prioritizationFramework !== 'IE'">
                <template v-if="!data.item.editing || data.item.editingField !== 'prioritization_confidence'">
                    <div @click="startEditing(data.item, 'prioritization_confidence')" style="cursor: pointer">
                        {{ data.item.prioritization_confidence }}%
                    </div>
                </template>
                <div v-else>
                    <b-form-input type="number" v-model.number="data.item.prioritization_confidence"
                                  min="1" max="100" step="1"
                                  @blur="validateAndUpdate($event, data.item, 'prioritization_confidence')"
                                  @keyup.enter="validateAndUpdate($event, data.item, 'prioritization_confidence')"></b-form-input>
                    <small class="text-danger">{{ getErrorMessage(data.item, 'prioritization_confidence') }}</small>
                </div>
            </template>

            <template #cell(prioritization_ease)="data">
                <template v-if="!data.item.editing || data.item.editingField !== 'prioritization_ease'">
                    <div @click="startEditing(data.item, 'prioritization_ease')" style="cursor: pointer">
                        <i v-for="i in 5" :key="i" class="bi bi-circle-fill mr-1"
                           :class="i <= Math.round((data.item.prioritization_ease || 0) / 2) ? 'text-danger' : 'text-muted'"></i>
                    </div>
                </template>
                <div v-else>
                    <b-form-input type="number" v-model.number="data.item.prioritization_ease"
                                  min="1" max="10" step="1"
                                  @blur="validateAndUpdate($event, data.item, 'prioritization_ease')"
                                  @keyup.enter="validateAndUpdate($event, data.item, 'prioritization_ease')"></b-form-input>
                    <small class="text-danger">{{ getErrorMessage(data.item, 'prioritization_ease') }}</small>
                </div>
            </template>

            <template #cell(moscow_priority)="data" v-if="prioritizationFramework === 'MoSCoW'">
                <b-form-select v-model="data.item.moscow_priority" :options="moscowOptions" @change="updateMoscowPriority(data.item)"></b-form-select>
            </template>

            <template #cell(color)="data">
                <div @click="startEditingColor(data.item)" :style="{ backgroundColor: data.item.prioritization_color, width: '20px', height: '20px', border: '1px solid #cccccc', cursor: 'pointer' }"></div>
                <div v-if="data.item.editingColor" class="mt-2">
                    <b-form-select v-model="data.item.prioritization_color" :options="colorOptions" @change="updateColor(data.item)"></b-form-select>
                    <b-button @click="$bvModal.show('edit-color-settings-modal')" variant="link" class="small text-muted px-0">Edit Color Settings</b-button>
                </div>
            </template>

            <template #cell(score)="data">
                <template v-if="canCalculateScore(data.item)">
                    <span class="badge badge-outline">{{ calculateScore(data.item).toFixed(2) }}</span>
                </template>
                <template v-else>
                    <span class="text-muted small">Incomplete</span>
                </template>
            </template>
        </b-table>

        <b-modal id="edit-statuses-modal" title="Edit Prioritization Statuses" @ok="saveStatuses" cancel-variant="outline">
            <b-form-group label="Select statuses for prioritization:">
                <b-form-checkbox-group v-model="editingStatuses" :options="statusOptions"></b-form-checkbox-group>
            </b-form-group>
        </b-modal>

        <b-modal id="edit-color-settings-modal" title="Edit Color Settings" @ok="saveColorSettings" cancel-variant="outline">
            <p class="mb-3">
                These color settings allow you to customize the status indicators for your prioritization.
                Each color represents a different status (e.g., draft, reviewed, done).
                Add, edit, or remove colors to match your workflow needs.
            </p>
            <div v-for="(color, name) in colorSettings" :key="name" class="mb-2">
                <b-input-group>
                    <b-form-input v-model="colorSettings[name].name" placeholder="Color name"></b-form-input>
                    <b-form-input v-model="colorSettings[name].hex" type="color" class="mx-2"></b-form-input>
                    <b-input-group-append>
                        <b-button @click="deleteColorSetting(name)" variant="outline">
                            <b-icon icon="trash" class="text-danger"></b-icon>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </div>
            <b-button @click="addColorSetting" variant="outline-primary" class="mt-2">Add Color</b-button>
        </b-modal>

        <b-button @click="$bvModal.show('edit-color-settings-modal')" variant="outline" class="mt-3">Edit Color Settings</b-button>
        </div>
    </div>
</template>

<script>
import Loading from "./Loading";
import AddPostModal
    from "./AddPostModal";
import {
    mapState
} from "vuex";

export default {
    components: {
        Loading,
        AddPostModal
    },
    data() {
        return {
            prioritizationFramework: 'RICE',
            frameworkOptions: [
                { value: 'RICE', text: 'RICE' },
                { value: 'ICE', text: 'ICE' },
                { value: 'IE', text: 'Impact Effort' },
                { value: 'MoSCoW', text: 'MoSCoW' }
            ],
            moscowOptions: [
                { value: 'M', text: 'Must Have' },
                { value: 'S', text: 'Should Have' },
                { value: 'C', text: 'Could Have' },
                { value: 'W', text: "Won't Have" },
                { value: null, text: "- Not set -" }
            ],
            selectedStatuses: [],
            editingStatuses: [],
            statusOptions: [],
            posts: [],
            showUnprioritized: false,
            newStatusId: null,
            newSectionId: null,
            categories: [],
            statuses: [],
            isAdmin: true, // Adjust as needed
            fields: [],
            loading: true,
            searchQuery: '',
            sortField: '',
            sortOrder: 1, // 1 for ascending, -1 for descending
            moscowCounts: {
                M: 0,
                S: 0,
                C: 0,
                W: 0
            },
            colorSettings: {},
            isFullScreen: false,
        };
    },
    mounted() {
        document.addEventListener("fullscreenchange", this.onFullScreenChange);
        document.addEventListener("webkitfullscreenchange", this.onFullScreenChange);
        document.addEventListener("mozfullscreenchange", this.onFullScreenChange);
        document.addEventListener("MSFullscreenChange", this.onFullScreenChange);
    },
    beforeDestroy() {
        document.removeEventListener("fullscreenchange", this.onFullScreenChange);
        document.removeEventListener("webkitfullscreenchange", this.onFullScreenChange);
        document.removeEventListener("mozfullscreenchange", this.onFullScreenChange);
        document.removeEventListener("MSFullscreenChange", this.onFullScreenChange);
    },
    computed: {
        frameworkDescription() {
            if (this.prioritizationFramework === 'MoSCoW') {
                return 'Use this prioritization module to identify which features you should build next. The MoSCoW framework is selected, which categorizes features into Must Have, Should Have, Could Have, and Won\'t Have.';
            }
            return `Use this prioritization module to identify which features you should build next. The ${this.prioritizationFramework} framework is selected, which is a popular framework and uses ${this.prioritizationFramework === 'RICE' ? 'Reach, Impact, Confidence, and Effort' : this.prioritizationFramework === 'ICE' ? 'Impact, Confidence, and Effort' : 'Impact and Effort'} to calculate priority scores.`;
        },
        filteredPosts() {
            let filtered = this.posts;

            if (this.searchQuery) {
                filtered = filtered.filter(post =>
                    post.title.toLowerCase().includes(this.searchQuery.toLowerCase())
                );
            }

            if (this.showUnprioritized) {
                filtered = filtered.filter(post => this.calculateScore(post) === 0);
            }

            return filtered;
        },
        colorOptions() {
            return Object.entries(this.colorSettings).map(([key, value]) => ({
                value: value.hex,
                text: value.name
            }));
        },
        totalCount() {
            return this.posts.length;
        },
        prioritizedCount() {
            return this.posts.filter(post => {
                if (this.prioritizationFramework === 'MoSCoW') {
                    return post.moscow_priority;
                } else {
                    return this.canCalculateScore(post);
                }
            }).length;
        },
        ...mapState(['defaultStatusId']),
    },
    created() {
        this.fetchSettings();
        this.fetchStatuses();

        this.$http.get('/http_api/categories')
            .then(({data}) => {
                this.loading = false;
                this.categories = data.data
            })
            .catch(error => {
                this.loading = false;
                this.popNotification('Something went wrong on our side. Could not load. Please try again or contact ruben@productlift.dev', 'danger')
            });
        this.$http.get(`/http_api/statuses`)
            .then((response) => {
                this.statuses = response.data.data;
            })
            .catch((error) => {
                this.popNotification('Failed to fetch statuses.', 'danger');
            });
    },
    methods: {
        fetchSettings() {
            this.$http.get('/http_api/prioritization-settings')
                .then((response) => {
                    this.prioritizationFramework = response.data.framework;
                    this.selectedStatuses = response.data.statuses || [];
                    this.editingStatuses = [...this.selectedStatuses];
                    this.colorSettings = response.data.prioritization_color_settings || [];
                    this.updateFields();
                    this.fetchPosts();

                    // Set newStatusId to the first status id
                    if (this.editingStatuses.length > 0) {
                        this.newStatusId = this.editingStatuses[0];
                    }
                })
                .catch((error) => {
                    this.popNotification('Failed to fetch prioritization settings.', 'danger');
                });
        },
        fetchStatuses() {
            this.$http.get(`/http_api/statuses`)
                .then((response) => {
                    this.statusOptions = response.data.data.map(status => ({
                        value: status.id,
                        text: status.name,
                    }));
                })
                .catch((error) => {
                    this.popNotification('Failed to fetch statuses.', 'danger');
                });
        },
        openAddPostModal() {
            this.$refs.addPostModal.isModalVisible = true;
        },
        onPostAdded(post) {
            this.fetchPosts();
        },
        fetchPosts() {
            const params = {
                limit: 100,
                status: this.selectedStatuses,
            };
            this.$http.get('/http_api/posts', { params })
                .then((response) => {
                    this.posts = response.data.data.map(post => ({
                        ...post,
                        editing: false,
                        editingField: null,
                        editingColor: false,
                        color: post.color || '#FFFFFF'
                    }));
                    this.loading = false;
                    this.updateMoscowCounts();
                })
                .catch((error) => {
                    this.popNotification('Failed to fetch posts.', 'danger');
                });
        },
        setFramework(newFramework) {
            this.prioritizationFramework = newFramework;
            this.updateSettings();
            this.updateFields();
        },
        validateAndUpdate(event, item, field) {
            const value = parseFloat(event.target.value);
            const isValid = this.validateField(value, field);
            if (isValid) {
                this.updateScore(item);
                item.editing = false;
                item.editingField = null;
            } else {
                item[field] = null;
            }
        },
        validateField(value, field) {
            if (isNaN(value)) return false;
            if (field === 'prioritization_confidence') {
                return value >= 1 && value <= 100;
            } else {
                return value >= 1 && value <= 10;
            }
        },
        getErrorMessage(item, field) {
            if (item[field] === null) return '';
            if (!this.validateField(item[field], field)) {
                if (field === 'prioritization_confidence') {
                    return 'Must be between 1 and 100';
                } else {
                    return 'Must be between 1 and 10';
                }
            }
            return '';
        },
        updateFields() {
            this.fields = [
                { key: 'title', label: 'Post Title' },
                { key: 'votes_and_conversion', label: 'Votes & Conversion' },
            ];

            if (this.prioritizationFramework === 'RICE') {
                this.fields.push(
                    { key: 'prioritization_impact', label: 'Impact' },
                    { key: 'prioritization_reach', label: 'Reach' },
                    { key: 'prioritization_confidence', label: 'Confidence' },
                    { key: 'prioritization_ease', label: 'Effort' }
                );
            } else if (this.prioritizationFramework === 'ICE') {
                this.fields.push(
                    { key: 'prioritization_impact', label: 'Impact' },
                    { key: 'prioritization_confidence', label: 'Confidence' },
                    { key: 'prioritization_ease', label: 'Ease' }
                );
            } else if (this.prioritizationFramework === 'IE') {
                this.fields.push(
                    { key: 'prioritization_impact', label: 'Impact' },
                    { key: 'prioritization_ease', label: 'Effort' }
                );
            } else if (this.prioritizationFramework === 'MoSCoW') {
                this.fields.push(
                    { key: 'moscow_priority', label: 'MoSCoW Priority' }
                );
            }

            this.fields.push(
                { key: 'color', label: 'Color' },
                { key: 'score', label: 'Score' }
            );
        },
        canCalculateScore(item) {
            if (this.prioritizationFramework === 'RICE') {
                return item.prioritization_reach && item.prioritization_impact &&
                    item.prioritization_confidence && item.prioritization_ease;
            } else if (this.prioritizationFramework === 'ICE') {
                return item.prioritization_impact && item.prioritization_confidence &&
                    item.prioritization_ease;
            } else if (this.prioritizationFramework === 'IE') {
                return item.prioritization_impact && item.prioritization_ease;
            } else if (this.prioritizationFramework === 'MoSCoW') {
                return item.moscow_priority;
            }
        },
        updateScore(item) {
            item.editing = false;
            item.editingField = null;
            item.editingColor = false;
            this.$http.post(`/http_api/posts/${item.id}/prioritize`, {
                prioritization_impact: parseInt(item.prioritization_impact) || null,
                prioritization_confidence: parseInt(item.prioritization_confidence) || null,
                prioritization_ease: parseInt(item.prioritization_ease) || null,
                prioritization_reach: parseInt(item.prioritization_reach) || null,
                moscow_priority: item.moscow_priority,
                prioritization_color: item.prioritization_color,
            })
                .then((response) => {
                    Object.assign(item, response.data.data);
                    this.$set(item, 'score', this.calculateScore(item));
                    this.updateMoscowCounts();
                })
                .catch((error) => {
                    this.popNotification('Failed to update post score.', 'danger');
                });
        },
        calculateScore(item) {
            if (this.prioritizationFramework === 'RICE') {
                return ((item.prioritization_reach || 0) *
                        (item.prioritization_impact || 0) *
                        ((item.prioritization_confidence || 0) / 100)) /
                    (item.prioritization_ease || 1);
            } else if (this.prioritizationFramework === 'ICE') {
                return ((item.prioritization_impact || 0) *
                    ((item.prioritization_confidence || 0) / 10) *
                    (item.prioritization_ease || 1));
            } else if (this.prioritizationFramework === 'IE') {
                return (item.prioritization_impact || 0) * (item.prioritization_ease || 0);
            } else if (this.prioritizationFramework === 'MoSCoW') {
                const moscowPriorities = { 'M': 4, 'S': 3, 'C': 2, 'W': 1 };
                return moscowPriorities[item.moscow_priority] || 0;
            }
        },
        startEditing(item, field) {
            item.editing = true;
            item.editingField = field;
        },
        startEditingColor(item) {
            item.editingColor = !item.editingColor;
        },
        reorderByScore() {
            this.posts.sort((a, b) => this.calculateScore(b) - this.calculateScore(a));
        },
        toggleStatus(statusId) {
            const index = this.selectedStatuses.indexOf(statusId);
            if (index > -1) {
                this.selectedStatuses.splice(index, 1);
            } else {
                this.selectedStatuses.push(statusId);
            }
            this.updateSettings();
        },
        saveStatuses() {
            this.selectedStatuses = this.editingStatuses;
            this.updateSettings();
        },
        updateSettings() {
            let settings = {
                statuses: this.selectedStatuses,
                framework: this.prioritizationFramework,
                prioritization_color_settings: this.colorSettings
            };
            this.$http.post('/http_api/prioritization-settings', settings)
                .then((response) => {
                    this.prioritizationFramework = response.data.framework;
                    this.selectedStatuses = response.data.statuses;
                    this.colorSettings = response.data.prioritization_color_settings;
                    this.updateFields();
                    this.fetchPosts();
                    this.popNotification('Prioritization settings updated successfully.', 'success');
                })
                .catch((error) => {
                    this.popNotification('Failed to update prioritization settings.', 'danger');
                });
        },
        updateMoscowCounts() {
            this.moscowCounts = {
                M: 0,
                S: 0,
                C: 0,
                W: 0
            };
            this.posts.forEach(post => {
                if (post.moscow_priority) {
                    this.moscowCounts[post.moscow_priority]++;
                }
            });
        },
        updateMoscowPriority(item) {
            this.updateScore(item);
        },
        updateColor(item) {
            this.updateScore(item);
        },
        saveColorSettings() {
            this.updateSettings();
        },
        addColorSetting() {
            const newColorKey = `color_${Object.keys(this.colorSettings).length + 1}`;
            this.$set(this.colorSettings, newColorKey, { name: '', hex: '#000000' });
        },
        sortByField(field) {
            if (this.sortField === field) {
                this.sortOrder = this.sortOrder * -1;
            } else {
                this.sortField = field;
                this.sortOrder = 1;
            }

            this.posts.sort((a, b) => {
                let aValue, bValue;

                if (field === 'score') {
                    aValue = this.calculateScore(a);
                    bValue = this.calculateScore(b);
                } else if (field === 'votes_and_conversion') {
                    // You might want to decide whether to sort by votes, conversion rate, or a combination
                    aValue = a.votes_count || 0; // or a.computedConversionRate
                    bValue = b.votes_count || 0; // or b.computedConversionRate
                } else {
                    aValue = a[field];
                    bValue = b[field];
                }

                if (aValue == null) aValue = '';
                if (bValue == null) bValue = '';

                if (aValue > bValue) {
                    return this.sortOrder;
                } else if (aValue < bValue) {
                    return -this.sortOrder;
                } else {
                    return 0;
                }
            });
        },
        popNotification(message, type) {
            console.log(`${type}: ${message}`);
        },
        getMoscowPercentage(category) {
            if (this.prioritizedCount === 0) return 0;
            return ((this.moscowCounts[category] / this.prioritizedCount) * 100).toFixed(1);
        },
        deleteColorSetting(name) {
            this.$delete(this.colorSettings, name);
        },
        toggleFullScreen() {
            this.isFullScreen = !this.isFullScreen;
            if (this.isFullScreen) {
                document.documentElement.requestFullscreen();
            } else {
                document.exitFullscreen();
            }
        },
        onFullScreenChange() {
            if (!document.fullscreenElement) {
                // Exited fullscreen mode
                this.isFullScreen = false;
            } else {
                // Entered fullscreen mode
                this.isFullScreen = true;
            }
        },
        exportCurrentStatuses() {
            const statusIds = this.selectedStatuses.join('&status_id[]=');
            const exportUrl = `/admin/posts/all?export=csv&status_id[]=${statusIds}`;
            window.open(exportUrl, '_blank');
        },
    },
    watch: {
        prioritizationFramework() {
            this.updateFields();
        }
    },
};
</script>

<style scoped>
.btn-group-toggle > .btn {
    opacity: 0.6;
}
.btn-group-toggle > .btn:hover,
.btn-group-toggle > .btn:focus {
    opacity: 1;
}
.bi-circle-fill {
    cursor: pointer;
}

.table th {
    cursor: pointer;
    user-select: none;
}
.table th:hover {
    text-decoration: underline;
}

.sort-icon {
    opacity: 0;
    transition: opacity 0.2s;
}

th:hover .sort-icon {
    opacity: 1;
}

.fullscreen-component {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1030;
    background: white;
    overflow-y: auto;
    padding: 20px;
}
</style>